import { useEffect, useId, useState } from 'react';
import {
  Controller,
  type Control,
  type FieldValues,
  type Path,
} from 'react-hook-form';
import { Button, TagSelect } from '@skupreme/shared/ui';
import { FormItem } from '@skupreme/shared/ui/form';
import ShippingLabelService from '@utility/services/shipping-label';

type CarrierMetadata = { carrier: { value: string; name: string } };

/** Uses the unfiltered carrier catalog, not the currently eligible rate list. */
export function ExcludedCarriersField<T extends FieldValues>({
  control,
  disabled = false,
  scope,
  className = 'sk:mb-4',
}: {
  control: Control<T>;
  disabled?: boolean;
  scope: 'organization' | 'provider';
  className?: string;
}) {
  const id = useId();
  const [carriers, setCarriers] = useState<CarrierMetadata[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError(false);
    ShippingLabelService.metadata()
      .then(result => {
        if (active) setCarriers(result.data || []);
      })
      .catch(() => {
        if (active) setError(true);
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [attempt]);

  return (
    <div className={`sk:flex sk:flex-col sk:gap-1 ${className}`}>
      <Controller
        name={'exclude_carriers' as Path<T>}
        control={control}
        render={({ field, fieldState }) => {
          const values: string[] = field.value || [];
          const options = new Map(
            carriers.map(({ carrier }) => [
              carrier.value,
              { value: carrier.value, label: carrier.name },
            ]),
          );
          for (const value of values) {
            if (!options.has(value))
              options.set(value, { value, label: value });
          }
          return (
            <FormItem
              label="Excluded Carriers"
              error={fieldState.error?.message}
            >
              <span className="sk:flex sk:w-full sk:[&>*]:min-w-0 sk:[&>*]:flex-1">
                <TagSelect
                  aria-label="Excluded Carriers"
                  aria-describedby={`${id}-help`}
                  placeholder="No carriers excluded"
                  options={[...options.values()]}
                  values={values}
                  isDisabled={disabled || loading || error}
                  isLoading={loading}
                  isInvalid={!!fieldState.error}
                  onBlur={field.onBlur}
                  onChange={next => field.onChange(next)}
                  className="sk:w-full"
                />
              </span>
            </FormItem>
          );
        }}
      />
      {error && (
        <div
          role="alert"
          className="sk:flex sk:flex-wrap sk:items-center sk:gap-2 sk:text-xs sk:text-danger"
        >
          Could not load carriers. Existing exclusions are unchanged.{' '}
          <Button
            size="sm"
            isDisabled={disabled}
            onPress={() => setAttempt(value => value + 1)}
          >
            Retry
          </Button>
        </div>
      )}
      <p id={`${id}-help`} className="sk:m-0 sk:text-xs sk:text-fg-tertiary">
        Excludes selected carriers from future rate-shopping for this {scope}.
        Existing labels are not changed, refunded, or relabeled. Clearing this
        list does not remove exclusions set elsewhere.
        {scope === 'organization' &&
          ' Destination carrier overrides can take precedence over organization exclusions.'}
      </p>
    </div>
  );
}
